import _ from 'lodash';

import {
  isMarketingAccountConnected,
  isAccountingAccountConnected,
} from '@/utils/external-accounts';
import {
  isBasicUserDetailsStepComplete,
  isBasicBusinessDetailsStepComplete,
  isOffersStepComplete,
  isConnectSalesAccountsStepComplete,
  isConnectBankingStepComplete,
  isBankLinkConfirmationStepComplete,
  isUploadBankStatementsStepComplete,
  isBusinessDetailsComplete,
  isRegisteredAddressStepComplete,
  isBusinessFormationDocsStepComplete,
  isOwnersListStepComplete,
  isOwnersStepOneComplete,
  findIncompleteOwner,
  isAdvanceContractStepComplete,
  isBankContractStepComplete,
  isMissedStepsStepComplete,
} from '@/utils/onboarding-steps';
import { ONBOARDING_STAGE, ONBOARDING_2_ROUTES } from '@/data/onboarding';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import i18n from '@/plugins/i18n';
import { LocalStorage } from '@/utils/local-storage';

export default {
  state: {
    experimentName: '',
    featureFlags: {},
    steps: {},
    applicationProgress: [
      {
        stepLabel: () =>
          i18n.t('modules.onboardingModule.applicationProgress.requestFunding'),
        percentComplete: 0,
      },
      {
        stepLabel: () =>
          i18n.t('modules.onboardingModule.applicationProgress.selectOffer'),
        percentComplete: 0,
      },
      {
        stepLabel: () =>
          i18n.t(
            'modules.onboardingModule.applicationProgress.completeApplication',
          ),
        percentComplete: 0,
      },
      {
        stepLabel: () =>
          i18n.t('modules.onboardingModule.applicationProgress.receiveFunds'),
        percentComplete: 0,
      },
    ],
    stepGroups: {},
    isRevisitingMissedStep: false,
  },
  getters: {
    onboardingSteps: (state) => state.steps,
    applicationProgress: (state) => state.applicationProgress,
    stepGroups: (state) => state.stepGroups,
    isRevisitingMissedStep: (state) => state.isRevisitingMissedStep,
    firstIncompleteStep: (state, getters) => {
      return {
        preOffer: Object.values(getters.onboardingSteps).find(
          (val) => !val.complete && val.stage === ONBOARDING_STAGE.PRE_OFFER,
        ),
        postOffer: Object.values(getters.onboardingSteps).find(
          (val) => !val.complete && val.stage === ONBOARDING_STAGE.POST_OFFER,
        ),
        all: Object.values(getters.onboardingSteps).find(
          (val) => !val.complete,
        ),
      };
    },
    missingSkippablePostOfferSteps: (state, getters) => {
      const stepsToExclude = [
        ONBOARDING_2_ROUTES.CONNECT_ACCOUNTING,
        ONBOARDING_2_ROUTES.BUSINESS_UPLOAD_EIN,
      ];
      if (getters.business.corpCountry === COUNTRY_CODES.DE)
        stepsToExclude.push(ONBOARDING_2_ROUTES.CONNECT_BANKING);
      const allMissedSteps = Object.values(getters.onboardingSteps).filter(
        (step) =>
          !step.complete &&
          step.stage === ONBOARDING_STAGE.POST_OFFER &&
          step.skippable === true &&
          !stepsToExclude.includes(step.name),
      );
      const allMissedStepNames = allMissedSteps.map((step) => {
        return step.name;
      });
      // if connect-banking and upload-docs steps are incomplete, user entirely skipped all banking steps
      if (
        allMissedStepNames.includes(ONBOARDING_2_ROUTES.CONNECT_BANKING) &&
        allMissedStepNames.includes(ONBOARDING_2_ROUTES.BANK_FAILED_UPLOAD_DOCS)
      ) {
        // so the Missed A Step page should only show the connect-banking (plaid) tile
        return allMissedSteps.filter(
          (step) =>
            ![
              ONBOARDING_2_ROUTES.BANK_LINK_CONFIRMATION,
              ONBOARDING_2_ROUTES.BANK_FAILED_UPLOAD_DOCS,
            ].includes(step.name),
        );
      }
      return allMissedSteps;
    },
    skippablePostOfferSteps: (state, getters) => {
      return Object.values(getters.onboardingSteps).filter(
        (step) => step.stage === ONBOARDING_STAGE.POST_OFFER && step.skippable,
      );
    },
  },
  actions: {
    SET_ONBOARDING_STEPS: ({ commit }, steps) => {
      commit('SET_ONBOARDING_STEPS', steps);
    },
    UPDATE_ALL_ONBOARDING_STEPS: ({ state, dispatch }) => {
      Object.keys(state.steps).forEach((step) =>
        dispatch('UPDATE_ONBOARDING_STEP', { step }),
      );
    },
    UPDATE_ONBOARDING_STEP: (
      { state, commit, rootGetters, getters },
      { step, wasSkipped },
    ) => {
      function findCompleted(currentStep) {
        switch (currentStep) {
          case 'onboarding-unified':
            return true;
          case ONBOARDING_2_ROUTES.SIGN_UP:
            return true;
          case ONBOARDING_2_ROUTES.BASIC_USER_DETAILS:
            return isBasicUserDetailsStepComplete(rootGetters.user);
          case ONBOARDING_2_ROUTES.BASIC_BUSINESS_DETAILS:
            return isBasicBusinessDetailsStepComplete(rootGetters.business);
          case ONBOARDING_2_ROUTES.CONNECT_SALES:
            return isConnectSalesAccountsStepComplete(
              rootGetters.externalAccounts,
            );
          case ONBOARDING_2_ROUTES.SELECT_PRODUCT:
            return rootGetters.advances.length;
          case ONBOARDING_2_ROUTES.WAITING_ROOM:
            return true;
          case ONBOARDING_2_ROUTES.OFFER_SELECTION:
            return isOffersStepComplete(
              rootGetters.advanceInNegotiation,
              rootGetters.business,
            );
          case ONBOARDING_2_ROUTES.CONNECT_MARKETING:
            return isMarketingAccountConnected(rootGetters.externalAccounts);
          case ONBOARDING_2_ROUTES.CONNECT_ACCOUNTING:
            return isAccountingAccountConnected(rootGetters.externalAccounts);
          case ONBOARDING_2_ROUTES.CONNECT_BANKING:
            return isConnectBankingStepComplete(rootGetters);
          case ONBOARDING_2_ROUTES.BANK_LINK_CONFIRMATION:
            return (
              isBankLinkConfirmationStepComplete(rootGetters) ||
              rootGetters.hasUploadedBankDocs
            );
          case ONBOARDING_2_ROUTES.BANK_FAILED_UPLOAD_DOCS:
            return isUploadBankStatementsStepComplete(rootGetters);
          case ONBOARDING_2_ROUTES.BUSINESS_DETAILS:
            return isBusinessDetailsComplete(rootGetters.business);
          case ONBOARDING_2_ROUTES.BUSINESS_REGISTERED_ADDRESS:
            return (
              isRegisteredAddressStepComplete(rootGetters.business) &&
              isBusinessDetailsComplete(rootGetters.business)
            );
          case ONBOARDING_2_ROUTES.BUSINESS_INCORP_DOCS:
            return isBusinessFormationDocsStepComplete(rootGetters);
          case ONBOARDING_2_ROUTES.OWNERS_STEP_ONE:
            return isOwnersStepOneComplete(
              rootGetters.allBusinessProfiles,
              rootGetters.uploads,
            );
          case ONBOARDING_2_ROUTES.OWNERS_STEP_TWO:
            return !findIncompleteOwner(rootGetters.allBusinessProfiles);
          case ONBOARDING_2_ROUTES.IDENTITY_VERIFICATION:
            return true;
          case ONBOARDING_2_ROUTES.OWNERS_LIST:
            return (
              isOwnersListStepComplete(
                rootGetters.user,
                rootGetters.authorizedSignatory,
              ) && isBusinessFormationDocsStepComplete(rootGetters)
            );
          case ONBOARDING_2_ROUTES.SIGN_ADVANCE_CONTRACT:
            return isAdvanceContractStepComplete(rootGetters);
          case ONBOARDING_2_ROUTES.SIGN_ACH_CONTRACT:
            return isBankContractStepComplete(rootGetters);
          case ONBOARDING_2_ROUTES.APPLICATION_CONFIRMATION:
            return true;
          case ONBOARDING_2_ROUTES.MISSED_STEPS:
            return isMissedStepsStepComplete(
              getters.missingSkippablePostOfferSteps,
            );
          default:
            return false;
        }
      }

      const updatedStep = {
        ...state.steps[step],
        complete: findCompleted(step),
      };

      if (wasSkipped === undefined) {
        const storedSkip = LocalStorage.getItem(
          LocalStorage.getSkippedStepName(step),
        );
        if (storedSkip) {
          updatedStep.wasSkipped = storedSkip;
        }
      } else {
        updatedStep.wasSkipped = wasSkipped;
        LocalStorage.setItem(LocalStorage.getSkippedStepName(step), wasSkipped);
      }

      if (findCompleted(ONBOARDING_2_ROUTES.MISSED_STEPS)) {
        commit('SET_REVISITING_MISSED_STEP', false);
      }

      commit('UPDATE_ONBOARDING_STEP', updatedStep);
    },
  },
  mutations: {
    SET_ONBOARDING_STEPS: (state, steps) => {
      state.steps = steps;
    },
    UPDATE_ONBOARDING_STEP: (state, updatedStep) => {
      state.steps[updatedStep.name] = updatedStep;
    },
    SET_NAVIGATION_PREVIEW: (state, stepGroups) => {
      state.stepGroups = stepGroups;
    },
    SET_REVISITING_MISSED_STEP: (state, status) => {
      state.isRevisitingMissedStep = status;
    },
  },
};
